import { Button, MarkdownPreview, Spinner, TextArea } from '@consigli/facade';
import { SUMMARY_SEGMENTS_NOT_READY } from '@consigli/types';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCalendar, FaClock, FaMapMarkerAlt } from 'react-icons/fa';

import { Agreement } from './agreement';
import { AgreementContradiction } from './agreement-contradiction';
import {
  ActionItemsTab,
  CopyButton,
  ErrorAlert,
  HintBox,
  TranscriptEntry,
  TranscriptStatusBanner,
} from './components';
import { EditableSummarySection } from './components/editable-summary-section';
import { autoSize } from './components/inline-editable-text';
import { ParticipantsPanel } from './components/participants-panel';
import { SummaryMetadata } from './components/summary-metadata';
import { DocumentContradiction } from './document-contradiction';
import { toUTC } from './format-meeting-datetime';
import { useMeetingExports } from './hooks/use-meeting-export';
import { useMeetingRole } from './hooks/use-meeting-role';
import { useMeetingSummaryData } from './hooks/use-meeting-summary-data';
import { useTextOnlyTranscript } from './hooks/use-text-only-transcript';
import { formatTranscriptForCopy, formatSummaryForCopy, getTranscriptBannerState } from './utils';
import {
  findPointBeforeTopic,
  keyPointsToText,
  parseKeyPointsText,
  textToKeyPoints,
} from './utils/text-utils';

import type { MeetingSummaryData } from './types';
import type { Meeting } from './utils/export-types';

export type { Meeting };

type ActiveTab =
  | 'summary'
  | 'transcript'
  | 'actions'
  | 'agreements'
  | 'agreementsContradiction'
  | 'documentContradiction';

interface MeetingSummaryProps {
  meeting: Meeting;
  onDataUpdate?: (data: MeetingSummaryData) => void;
}

interface MeetingDetailsProps {
  meeting: Meeting;
}

export const MeetingDetails: React.FC<MeetingDetailsProps> = ({ meeting }) => {
  const { t, i18n } = useTranslation();
  if (!meeting) return null;

  const startDate = new Date(toUTC(meeting.scheduledStartTime));
  const endDate = new Date(toUTC(meeting.scheduledEndTime));
  const duration = Math.round((endDate.getTime() - startDate.getTime()) / 60000);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        {t('meeting-secretary.meetings.meeting-details')}
      </h2>

      <div className="space-y-4">
        <div className="flex items-center space-x-3">
          <div className="w-5 h-5 text-gray-500">
            <FaCalendar />
          </div>
          <span className="text-gray-900">
            {startDate.toLocaleDateString(i18n.language, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </span>
        </div>

        <div className="flex items-center space-x-3">
          <div className="w-5 h-5 text-gray-500">
            <FaClock />
          </div>
          <span className="text-gray-900">
            {startDate.toLocaleTimeString(i18n.language, {
              hour: '2-digit',
              minute: '2-digit',
            })}
            ({duration} {t('meeting-secretary.meetings.minutes')})
          </span>
        </div>

        {meeting.location && (
          <div className="flex items-center space-x-3">
            <div className="w-5 h-5 text-gray-500">
              <FaMapMarkerAlt />
            </div>
            <span className="text-gray-900">{meeting.location}</span>
          </div>
        )}

        <div>
          <div className="flex items-center space-x-3 mb-3" />
          <div className="ml-8 space-y-2">
            <div className="text-sm">
              <span className="font-medium">{meeting.organizerEmail ?? ''}</span>
              <span className="text-gray-500 ml-2">
                - {t('meeting-secretary.meetings.organizer')}
              </span>
            </div>
          </div>
        </div>
      </div>

      <ParticipantsPanel meeting={meeting} />
    </div>
  );
};

export const MeetingSummary: React.FC<MeetingSummaryProps> = ({ meeting, onDataUpdate }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<ActiveTab>('transcript');
  // Drafts live here, above the tab content, so they survive switching tabs.
  // `null` means the section is not being edited.
  const [summaryDraft, setSummaryDraft] = useState<string | null>(null);
  const [keyPointsDraft, setKeyPointsDraft] = useState<string | null>(null);

  useEffect(() => {
    setSummaryDraft(null);
    setKeyPointsDraft(null);
  }, [meeting.id]);

  const {
    transcripts,
    parsedTranscripts,
    actionItems,
    agreements,
    agreementContradictions,
    agreementContradictionsHasFetched,
    documentContradictions,
    documentContradictionsHasFetched,
    documentContradictionsRetriable,
    summary,
    error,
    errorType,
    transcriptInfoMessage,
    canRegenerateWithAudio,
    audioUnavailable,
    summaryErrorType,
    actionItemsError,
    selectedDocumentPackageId,
    projectPackages,
    participants,
    assignSpeaker,
    isTranscriptLoading,
    isSummaryLoading,
    isSummaryGenerating,
    isAgreementsLoading,
    isAgreementsGenerating,
    isLoadingActionItems,
    isGeneratingActionItems,
    isLoadingContradictions,
    isGeneratingContradictions,
    isLoadingDocumentContradictions,
    isGeneratingDocumentContradictions,
    setActionItems,
    setAgreements,
    setError,
    buildParams,
    fetchTranscripts,
    applyTranscripts,
    resetGeneratedData,
    fetchSummary,
    saveSummary,
    fetchActionItems,
    fetchAgreements,
    fetchAgreementContradictions,
    fetchDocumentContradictions,
    handleDocumentPackageChange,
    fetchAllData,
  } = useMeetingSummaryData(meeting);

  const {
    uploadTranscript,
    isUploading,
    uploadError,
    uploadErrorDetails,
    clearUploadError,
    regenerateWithAudio,
    isRegenerating,
  } = useTextOnlyTranscript({
    meeting,
    buildParams,
    fetchTranscripts,
    applyTranscripts,
    resetGeneratedData,
  });

  const handleRegenerate = async () => {
    if (await regenerateWithAudio()) {
      // The drafts were written against the summary that was just replaced.
      setSummaryDraft(null);
      setKeyPointsDraft(null);
    }
  };
  // A generation that finishes during the audio run can survive the backend's clean-up.
  const isGeneratingFromTranscript =
    isSummaryLoading ||
    isLoadingActionItems ||
    isGeneratingActionItems ||
    isAgreementsLoading ||
    isAgreementsGenerating;

  const role = useMeetingRole(meeting);
  const bannerState = getTranscriptBannerState({
    transcripts,
    messageKey: transcriptInfoMessage,
    isProcessing: errorType === 'processing',
    role,
    canRegenerateWithAudio,
    audioUnavailable,
  });

  // What the key points editor shows when it opens; the baseline for which
  // numbers the user changed.
  const savedKeyPointsText = keyPointsToText(summary?.keyDiscussionPointsText);

  useEffect(() => {
    if (meeting) {
      fetchTranscripts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting]);

  useEffect(() => {
    if (!meeting) return;

    setError(null);

    const controller = new AbortController();
    const { signal } = controller;

    if (activeTab === 'summary') {
      fetchSummary();
    } else if (activeTab === 'actions') {
      fetchActionItems(signal);
    } else if (activeTab === 'agreements') {
      fetchAgreements(signal);
    } else if (activeTab === 'agreementsContradiction' || activeTab === 'documentContradiction') {
      // Contradictions are found between agreements, so they exist first: a re-run with audio
      // leaves none behind until they are generated again, in this order.
      const run = async () => {
        if (agreements.length === 0) await fetchAgreements(signal);
        if (signal.aborted) return;
        if (activeTab === 'agreementsContradiction') {
          await fetchAgreementContradictions(signal);
        } else {
          await fetchDocumentContradictions(undefined, signal);
        }
      };
      void run();
    }

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, meeting, transcripts]);

  const {
    handleExportPdf,
    handleExportDocx,
    handleExportExcel,
    isExportingPdf,
    isExportingDocx,
    isExportingExcel,
  } = useMeetingExports({
    meeting,
    parsedTranscripts,
    fetchAllData,
  });

  useEffect(() => {
    if (onDataUpdate) {
      onDataUpdate({
        summary,
        actionItems,
        agreements,
        agreementContradictions,
        documentContradictions,
        isDataReady: transcripts.length > 0,
        fetchAllData,
        handleExportPdf,
        handleExportDocx,
        handleExportExcel,
        isExportingPdf,
        isExportingDocx,
        isExportingExcel,
      });
    }
  }, [
    onDataUpdate,
    summary,
    actionItems,
    agreements,
    agreementContradictions,
    documentContradictions,
    transcripts.length,
    fetchAllData,
    handleExportPdf,
    handleExportDocx,
    handleExportExcel,
    isExportingPdf,
    isExportingDocx,
    isExportingExcel,
  ]);

  const noTranscriptsTooltip = t('meeting-secretary.meetings.no-transcripts-available');
  const hasTranscripts = transcripts.length > 0;

  const tabs: { key: ActiveTab; label: string; disabled?: boolean }[] = [
    { key: 'transcript', label: t('meeting-secretary.meetings.transcript') },
    { key: 'summary', label: t('meeting-secretary.meetings.summary'), disabled: !hasTranscripts },
    {
      key: 'actions',
      label: t('meeting-secretary.meetings.action-items'),
      disabled: !hasTranscripts,
    },
    {
      key: 'agreements',
      label: t('meeting-secretary.meetings.agreements', 'Agreements'),
      disabled: !hasTranscripts,
    },
    {
      key: 'agreementsContradiction',
      label: t('meeting-secretary.meetings.agreements-contradiction', 'Agreement Contradictions'),
      disabled: !hasTranscripts,
    },
    {
      key: 'documentContradiction',
      label: t('meeting-secretary.meetings.document-contradictions', 'Document Contradictions'),
      disabled: !hasTranscripts,
    },
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {bannerState && (
        <div className="mb-6">
          <TranscriptStatusBanner
            state={bannerState}
            organizerEmail={meeting.organizerEmail}
            onCheckAgain={() => fetchTranscripts()}
            onUpload={uploadTranscript}
            isUploading={isUploading}
            uploadError={uploadError}
            uploadErrorDetails={uploadErrorDetails}
            onClearUploadError={clearUploadError}
            onRegenerate={handleRegenerate}
            isRegenerating={isRegenerating}
            regenerateDisabled={isGeneratingFromTranscript}
          />
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          {tabs.find((tab) => tab.key === activeTab)?.label ||
            t('meeting-secretary.meetings.meeting-summary')}
        </h2>
      </div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => !tab.disabled && setActiveTab(tab.key)}
              disabled={tab.disabled}
              title={tab.disabled ? noTranscriptsTooltip : undefined}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                tab.disabled
                  ? 'border-transparent text-gray-300 cursor-not-allowed'
                  : activeTab === tab.key
                    ? 'border-[#475569] text-[#475569] cursor-pointer'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 cursor-pointer'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="space-y-6">
        {activeTab === 'summary' && (
          <div className="space-y-4">
            {isSummaryLoading && (
              <div className="flex items-center justify-center py-4">
                <Spinner size="xsmall" />
                <span className="ml-2 text-gray-600 text-sm">
                  {t(
                    isSummaryGenerating
                      ? 'meeting-secretary.meetings.generating-summary'
                      : 'meeting-secretary.meetings.loading-summary',
                  )}
                </span>
              </div>
            )}

            {summaryErrorType === SUMMARY_SEGMENTS_NOT_READY && (
              <ErrorAlert
                variant="warning"
                title={t('meeting-secretary.meetings.summary-segments-not-ready')}
                message={t('meeting-secretary.meetings.summary-segments-not-ready-description')}
              />
            )}

            {error && summaryErrorType !== SUMMARY_SEGMENTS_NOT_READY && (
              <div className="border rounded-lg p-4 bg-red-50 border-red-200">
                <div className="flex items-start space-x-3">
                  <div className="text-red-500">❌</div>
                  <div>
                    <h3 className="font-medium text-red-800">
                      {t('meeting-secretary.meetings.summary-generation-failed')}
                    </h3>
                    <p className="text-sm mt-1 text-red-700">{error}</p>
                    {transcripts.length > 0 && (
                      <Button error rounded className="mt-2" onClick={() => fetchSummary()}>
                        {t('meeting-secretary.meetings.try-again')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {!isSummaryLoading && !error && summary && (
              <div>
                <div className="flex justify-between items-start">
                  <SummaryMetadata
                    start={summary.start}
                    end={summary.end}
                    location={summary.location}
                  />
                  <CopyButton textToCopy={formatSummaryForCopy(summary)} />
                </div>
                {summary.summaryText === undefined ? (
                  // Older backend without the editable fields: read-only as before.
                  <MarkdownPreview
                    content={summary.generatedSummaryText}
                    className="mt-6 text-gray-700"
                  />
                ) : (
                  <div className="mt-6 space-y-6">
                    <EditableSummarySection
                      label={t('meeting-secretary.meetings.summary')}
                      editLabel={t('meeting-secretary.meetings.edit-summary')}
                      content={summary.summaryText}
                      isEditing={summaryDraft !== null}
                      onEdit={() => setSummaryDraft(summary.summaryText ?? '')}
                      onCancel={() => setSummaryDraft(null)}
                      onSave={async () => {
                        await saveSummary({ summaryText: (summaryDraft ?? '').trim() });
                        setSummaryDraft(null);
                      }}
                      canSave={
                        !!summaryDraft?.trim() && summaryDraft.trim() !== summary.summaryText
                      }
                    >
                      <TextArea
                        ref={(el) => el && autoSize(el)}
                        value={summaryDraft ?? ''}
                        rows={3}
                        onChange={(e) => setSummaryDraft(e.target.value)}
                        onInput={(e) => autoSize(e.currentTarget)}
                        aria-label={t('meeting-secretary.meetings.summary')}
                        className="text-sm resize-none overflow-hidden"
                      />
                    </EditableSummarySection>

                    <EditableSummarySection
                      label={t('meeting-secretary.meetings.key-discussion-points')}
                      editLabel={t('meeting-secretary.meetings.edit-key-discussion-points')}
                      content={summary.keyDiscussionPointsText ?? ''}
                      isEditing={keyPointsDraft !== null}
                      onEdit={() => setKeyPointsDraft(savedKeyPointsText)}
                      onCancel={() => setKeyPointsDraft(null)}
                      onSave={async () => {
                        const draft = keyPointsDraft ?? '';
                        const orphanLine = findPointBeforeTopic(draft);
                        if (orphanLine !== null) {
                          throw new Error(
                            t('meeting-secretary.meetings.key-points-error-point-before-topic', {
                              line: orphanLine,
                            }),
                          );
                        }
                        await saveSummary({
                          keyDiscussionPointsText: textToKeyPoints(draft, savedKeyPointsText),
                        });
                        setKeyPointsDraft(null);
                      }}
                      canSave={
                        !!keyPointsDraft &&
                        parseKeyPointsText(keyPointsDraft).length > 0 &&
                        textToKeyPoints(keyPointsDraft, savedKeyPointsText) !==
                          textToKeyPoints(savedKeyPointsText, savedKeyPointsText)
                      }
                    >
                      <TextArea
                        ref={(el) => el && autoSize(el)}
                        value={keyPointsDraft ?? ''}
                        rows={6}
                        onChange={(e) => setKeyPointsDraft(e.target.value)}
                        onInput={(e) => autoSize(e.currentTarget)}
                        aria-label={t('meeting-secretary.meetings.key-discussion-points')}
                        className="text-sm resize-none overflow-hidden"
                      />
                      <p className="text-xs text-gray-500">
                        {t('meeting-secretary.meetings.key-points-hint')}
                      </p>
                    </EditableSummarySection>
                  </div>
                )}
              </div>
            )}

            {!isSummaryLoading && !error && !summary && transcripts.length === 0 && (
              <div className="text-center py-8">
                <div className="text-gray-400 text-4xl mb-4">{'\uD83D\uDCDD'}</div>
                <p className="text-gray-500">
                  {t('meeting-secretary.meetings.no-transcripts-available')}
                </p>
                <p className="text-sm text-gray-400 mt-2">
                  {t('meeting-secretary.meetings.no-transcripts-summary-required')}
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'transcript' && (
          <div className="space-y-4">
            {isTranscriptLoading && (
              <div className="flex items-center justify-center py-4">
                <Spinner size="xsmall" />
                <span className="ml-2 text-gray-600 text-sm">
                  {t('meeting-secretary.meetings.loading-transcripts')}
                </span>
              </div>
            )}

            {/* Teams still processing is the banner's wait message, not an error. */}
            {error && errorType !== 'processing' && (
              <div
                className={`border rounded-lg p-4 ${
                  errorType === 'legacy' ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className={errorType === 'legacy' ? 'text-blue-500' : 'text-red-500'}>
                    {errorType === 'legacy' ? 'ℹ️' : '❌'}
                  </div>
                  <div>
                    <h3
                      className={`font-medium ${
                        errorType === 'legacy' ? 'text-blue-800' : 'text-red-800'
                      }`}
                    >
                      {errorType === 'legacy'
                        ? t('meeting-secretary.meetings.transcript-access-not-available')
                        : t('meeting-secretary.meetings.error-loading-transcripts')}
                    </h3>
                    <p
                      className={`text-sm mt-1 ${
                        errorType === 'legacy' ? 'text-blue-700' : 'text-red-700'
                      }`}
                    >
                      {error}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!isTranscriptLoading && !error && parsedTranscripts.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-gray-900">
                    {t('meeting-secretary.meetings.meeting-transcript')}
                  </h3>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-500">
                      {t('meeting-secretary.meetings.segments', {
                        count: parsedTranscripts.length,
                      })}
                    </span>
                    <CopyButton textToCopy={formatTranscriptForCopy(parsedTranscripts)} />
                  </div>
                </div>

                {isRegenerating && (
                  <HintBox variant="info">
                    {t('meeting-secretary.meetings.transcript-regenerate-in-progress')}
                  </HintBox>
                )}

                <div className="space-y-4 max-h-[calc(100vh-520px)] overflow-y-auto">
                  {parsedTranscripts.map((entry) => (
                    <TranscriptEntry
                      key={entry.segmentId ?? `${entry.timestamp}-${entry.speaker}`}
                      entry={entry}
                      participants={participants}
                      onAssignSpeaker={assignSpeaker}
                    />
                  ))}
                </div>
              </div>
            )}

            {!isTranscriptLoading &&
              !error &&
              transcripts.length > 0 &&
              parsedTranscripts.length === 0 && (
                <div className="text-center py-8">
                  <div className="text-gray-400 text-4xl mb-4">{'\uD83D\uDCDD'}</div>
                  <p className="text-gray-500">
                    {t('meeting-secretary.meetings.summary-segments-not-ready')}
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    {t('meeting-secretary.meetings.summary-segments-not-ready-description')}
                  </p>
                  <Button
                    secondary
                    rounded
                    className="mt-3 mx-auto"
                    onClick={() => fetchTranscripts()}
                  >
                    {t('meeting-secretary.meetings.try-again')}
                  </Button>
                </div>
              )}

            {!isTranscriptLoading && !error && transcripts.length === 0 && bannerState === null && (
              <div className="text-center py-8">
                <div className="text-gray-400 text-4xl mb-4">{'\uD83D\uDCDD'}</div>
                <p className="text-gray-500">
                  {t('meeting-secretary.meetings.no-transcripts-available')}
                </p>
                <p className="text-sm text-gray-400 mt-2">
                  {t('meeting-secretary.meetings.transcripts-appear-after')}
                </p>
                <Button
                  secondary
                  rounded
                  className="mt-3 mx-auto"
                  onClick={() => fetchTranscripts()}
                >
                  {t('meeting-secretary.meetings.try-again')}
                </Button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'actions' && (
          <ActionItemsTab
            loading={isLoadingActionItems}
            generating={isGeneratingActionItems}
            error={actionItemsError}
            actionItems={actionItems}
            hasTranscripts={transcripts.length > 0}
            transcriptEntries={parsedTranscripts}
            transcriptMeeting={meeting}
            participants={participants}
            meetingId={meeting.id}
            onRetry={() => fetchActionItems()}
            onActionItemsChange={setActionItems}
          />
        )}

        {activeTab === 'agreements' && (
          <Agreement
            loading={isAgreementsLoading}
            generating={isAgreementsGenerating}
            error={error}
            agreements={agreements}
            hasTranscripts={transcripts.length > 0}
            transcriptEntries={parsedTranscripts}
            meeting={meeting}
            participants={participants}
            onAgreementsChange={setAgreements}
            onRetry={() => fetchAgreements()}
          />
        )}

        {activeTab === 'agreementsContradiction' && (
          <AgreementContradiction
            loading={isLoadingContradictions}
            generating={isGeneratingContradictions}
            hasFetched={agreementContradictionsHasFetched}
            error={error}
            agreementContradictions={agreementContradictions}
            hasTranscripts={transcripts.length > 0}
            meeting={meeting}
            agreements={agreements}
            transcriptEntries={parsedTranscripts}
            onRetry={() => fetchAgreementContradictions()}
          />
        )}

        {activeTab === 'documentContradiction' && (
          <DocumentContradiction
            loading={isLoadingDocumentContradictions}
            generating={isGeneratingDocumentContradictions}
            hasFetched={documentContradictionsHasFetched}
            error={error}
            documentContradictions={documentContradictions}
            hasDocumentPackage={projectPackages.length > 0}
            canRetry={documentContradictionsRetriable}
            onRetry={() => fetchDocumentContradictions()}
            documentPackages={projectPackages}
            selectedPackageId={
              selectedDocumentPackageId ||
              meeting.documentPackageId ||
              projectPackages[0]?.id ||
              null
            }
            onPackageChange={handleDocumentPackageChange}
            meeting={meeting}
            agreements={agreements}
            transcriptEntries={parsedTranscripts}
          />
        )}
      </div>
    </div>
  );
};
